import type { BoundingBox, ListingSibling } from "@/types";
import { ZoomedListingImage } from "@/components/listings/ZoomedListingImage";
import { BboxStatusIcon } from "@/components/listings/BboxStatusIcon";

const SIBLING_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

interface ListingCardBboxOverlayProps {
  imageUrl: string;
  alt: string;
  boundingBox: BoundingBox;
  siblings?: ListingSibling[];
  className?: string;
  aspectRatio?: number;
}

function siblingColor(index: number, status: string): string {
  if (status === "cancelled") return "#9ca3af";
  if (status === "sold") return "#ef4444";
  if (status === "reserved") return "#f59e0b";
  return SIBLING_COLORS[(index + 1) % SIBLING_COLORS.length];
}

function boxesOverlap(a: BoundingBox, b: { x: number; y: number; w: number; h: number }, Wi: number, Hi: number): boolean {
  const ax = (a.xMin / 1000) * Wi;
  const ay = (a.yMin / 1000) * Hi;
  const aw = ((a.xMax - a.xMin) / 1000) * Wi;
  const ah = ((a.yMax - a.yMin) / 1000) * Hi;
  return ax < b.x + b.w && ax + aw > b.x && ay < b.y + b.h && ay + ah > b.y;
}

export function ListingCardBboxOverlay({
  imageUrl,
  alt,
  boundingBox,
  siblings,
  className,
  aspectRatio = 3 / 2,
}: ListingCardBboxOverlayProps) {
  return (
    <ZoomedListingImage
      imageUrl={imageUrl}
      boundingBox={boundingBox}
      alt={alt}
      className={className}
      aspectRatio={aspectRatio}
      paddingPx={12}
      overlayContent={({ Wi, Hi, crop }) => (
        <>
          {siblings?.map((sibling, i) => {
            if (!sibling.boundingBox) return null;
            if (sibling.status !== "sold" && sibling.status !== "reserved" && sibling.status !== "cancelled") {
              return null;
            }
            if (!boxesOverlap(sibling.boundingBox, crop, Wi, Hi)) return null;

            const bb = sibling.boundingBox;
            const bw = ((bb.xMax - bb.xMin) / 1000) * Wi;
            const bh = ((bb.yMax - bb.yMin) / 1000) * Hi;
            const cx = (bb.xMin / 1000) * Wi + bw / 2;
            const cy = (bb.yMin / 1000) * Hi + bh / 2;
            const color = siblingColor(i, sibling.status);

            return (
              <BboxStatusIcon
                key={sibling.id}
                cx={cx}
                cy={cy}
                status={sibling.status}
                color={color}
                size={Math.min(bw, bh) * 0.4}
                aspectRatio={Hi / Wi}
                minRadius={18}
              />
            );
          })}
        </>
      )}
    />
  );
}
